/**
 * Afroyla homepage — single JS island for all six chapter sections. Ported from the
 * approved sim (scratchpad/pw/t1msim-home.dc.html lines 828-1356) per
 * plans/20260820-horizon-rebuild/phase-05-homepage.md. Generalized in two ways the
 * sim's hand-rolled DCLogic component didn't need to be:
 *   1. `fit()` reads each hotspot's glow/name centre-x from a `data-cx` attribute
 *      (set from a block setting) instead of a hardcoded global CX map — makes the
 *      hotspot engine merchant-block-driven instead of product-name-keyed.
 *   2. Hover/caption-sync/connector-sizing are scoped per `[data-hs-photo]` container
 *      discovered from the DOM, not duplicated per section (the sim had separate
 *      near-identical `spot`/`spot3` functions for S2 vs S5 — one function here).
 * Dead code intentionally NOT ported: LOOKS4/LX4/BAND5/SLIDE_LEFT/SHELF consts and
 * their handlers, and all mega-nav/drawer state (`panel`, `bagHover`, `drawer`,
 * `lockScroll`) — that's phase-02/03 chrome, already implemented in afroyla-header.js.
 */
(function () {
  'use strict';

  // Every chapter section now embeds this same script tag (see afl-home__aflHomeInit
  // note in each section's <head> block) so the island survives a merchant removing
  // any one section in the editor. Guard against the resulting re-execution: only
  // the first tag to run wires the page; later tags see the flag and no-op.
  if (window.__aflHomeInit) return;
  window.__aflHomeInit = true;

  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* -----------------------------------------------------------------------------
     1. Hotspot-plate engine: replay the object-fit:cover math to convert each
        image-percent box/dot/anchor into container px. Runs on load + resize.
     ----------------------------------------------------------------------------- */
  function fitAll() {
    document.querySelectorAll('[data-hs-photo]').forEach(function (plate) {
      var img = plate.querySelector('img');
      if (!img || !img.naturalWidth) return;

      var cw = plate.clientWidth;
      var ch = plate.clientHeight;
      var s = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      var rw = img.naturalWidth * s;
      var rh = img.naturalHeight * s;
      var op = (plate.getAttribute('data-op') || '50 50').split(' ').map(Number);
      var ox = -((rw - cw) * op[0]) / 100;
      var oy = -((rh - ch) * op[1]) / 100;
      var boxes = {};

      plate.querySelectorAll('[data-box]').forEach(function (a) {
        var b = a.getAttribute('data-box').split(',').map(Number);
        var pad = Number(a.getAttribute('data-pad') || 8);
        a.style.left = ox + (b[0] / 100) * rw - pad + 'px';
        a.style.top = oy + (b[1] / 100) * rh - pad + 'px';
        a.style.width = ((b[2] - b[0]) / 100) * rw + pad * 2 + 'px';
        a.style.height = ((b[3] - b[1]) / 100) * rh + pad * 2 + 'px';
        var k = a.getAttribute('data-hover');
        if (k) {
          boxes[k] = {
            t: oy + (b[1] / 100) * rh,
            b: oy + (b[3] / 100) * rh,
            l: ox + (b[0] / 100) * rw,
            r: ox + (b[2] / 100) * rw,
          };
        }
      });

      plate.querySelectorAll('[data-dot]').forEach(function (a) {
        var d = a.getAttribute('data-dot').split(',').map(Number);
        a.style.left = ox + (d[0] / 100) * rw + 'px';
        a.style.top = oy + (d[1] / 100) * rh + 'px';
      });

      plate.querySelectorAll('[data-anchor]').forEach(function (el) {
        var k = el.getAttribute('data-anchor');
        var px = boxes[k];
        if (!px) return;
        var mode = el.getAttribute('data-mode');
        if (mode === 'box') {
          el.style.left = px.l - 40 + 'px';
          el.style.top = px.t - 40 + 'px';
          el.style.width = px.r - px.l + 80 + 'px';
          el.style.height = px.b - px.t + 80 + 'px';
        } else {
          var cx = Number(el.getAttribute('data-cx'));
          el.style.width = '280px';
          el.style.left = ox + (cx / 100) * rw - 140 + 'px';
          var gap = Number(el.getAttribute('data-gap') || (mode === 'below' ? 16 : 36));
          el.style.top = mode === 'below' ? px.b + gap + 'px' : px.t - gap + 'px';
        }
      });

      // Caption-band connectors: the band is the plate's own next sibling when the
      // hotspot-plate snippet rendered one (show_captions: true).
      var band = plate.nextElementSibling;
      if (band && band.hasAttribute('data-mediacap')) {
        band.querySelectorAll('[data-connect]').forEach(function (line) {
          var px = boxes[line.getAttribute('data-connect')];
          if (!px) return;
          var span = ch - px.b + 26;
          line.style.top = -span + 'px';
          line.style.height = span + 'px';
        });
      }
    });
  }

  /* -----------------------------------------------------------------------------
     2. Roster-plate hover: spotlight the hovered hotspot's glow/name, dim the img,
        underline its caption. Scoped per plate so S2/S5 (and any future plate)
        share one implementation.
     ----------------------------------------------------------------------------- */
  function wirePlateHover(plate) {
    var img = plate.querySelector('img');
    var hovers = plate.querySelectorAll('[data-hover]');
    if (!hovers.length) return;

    // Keys ("k0", "k1"...) are only unique per plate render, so caption lookups must
    // stay scoped to THIS plate's own caption band (its next sibling) — a page-wide
    // query would cross-contaminate S2's "k0" with S3's "k0".
    var band = plate.nextElementSibling;
    var scope = band && band.hasAttribute('data-mediacap') ? band : null;

    function spot(key) {
      if (img) img.style.filter = key ? 'brightness(.92)' : '';
      plate.querySelectorAll('[data-glow]').forEach(function (g) {
        g.classList.toggle('is-on', g.getAttribute('data-glow') === key);
      });
      plate.querySelectorAll('[data-name]').forEach(function (n) {
        n.classList.toggle('is-on', n.getAttribute('data-name') === key);
      });
    }

    function setCaptionUnderline(key, on) {
      if (!scope) return;
      scope.querySelectorAll('[data-cap="' + key + '"]').forEach(function (e) {
        e.classList.toggle('is-underline', on);
      });
    }

    hovers.forEach(function (a) {
      var k = a.getAttribute('data-hover');
      a.addEventListener('mouseenter', function () {
        spot(k);
        setCaptionUnderline(k, true);
      });
      a.addEventListener('mouseleave', function () {
        spot(null);
        setCaptionUnderline(k, false);
      });
    });
  }

  /* -----------------------------------------------------------------------------
     Shared once() + rise() — every animated group gets its OWN IntersectionObserver
     (threshold .15) + 1600ms fallback timer, disconnecting after it fires once.
     ----------------------------------------------------------------------------- */
  function once(target, run) {
    if (!target) return;
    var done = false;
    var io = null;
    // Whichever fires first (the observer or the fallback) disconnects/clears the
    // other — previously only the IO branch cleaned up, so a target that never
    // intersects (fallback fires) left its observer running forever.
    var timer = setTimeout(go, 1600);
    function go() {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (io) io.disconnect();
      run();
    }
    if (typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) go();
          });
        },
        { threshold: 0.15 }
      );
      io.observe(target);
    }
  }

  // `interval` defaults to 140ms (caption/dot roll-call stagger, sim lines
  // 1136-1142); the founder note passes 120ms (sim's own inline stagger loop,
  // lines 1189-1192) — was hardcoded to 140 for both, which desynced the
  // signature-wipe timer (`rows.length * 120`, unchanged) from the actual row
  // animation, so the wipe could start before the last founder row had risen.
  function rise(rows, lines, interval) {
    var step = interval || 140;
    rows.forEach(function (el, i) {
      setTimeout(function () {
        el.classList.add('is-in');
        var line = lines && lines[i];
        if (line) line.classList.add('is-in');
      }, i * step);
    });
  }

  function wireCaptionRise(band) {
    if (!band) return;
    var rows = Array.prototype.slice.call(band.querySelectorAll('[data-rise]'));
    if (!rows.length) return;
    var lines = rows.map(function (r) {
      var key = r.getAttribute('data-rise');
      return key ? band.querySelector('[data-connect="' + key + '"]') : null;
    });
    once(band, function () {
      rise(rows, lines);
    });
  }

  /* -----------------------------------------------------------------------------
     3. Looks row (chapter 03): hover/focus a panel grows it; leaving the row
        resets everything. Keyboard focus must trigger the same state as hover.
     ----------------------------------------------------------------------------- */
  function wireLooksRow(row) {
    if (!row) return;
    var panels = row.querySelectorAll('[data-look]');

    function open(target) {
      panels.forEach(function (p) {
        var on = p === target;
        p.classList.toggle('is-open', on);
        p.classList.toggle('is-dim', !on);
      });
    }

    function reset() {
      panels.forEach(function (p) {
        p.classList.remove('is-open', 'is-dim');
      });
    }

    panels.forEach(function (p) {
      p.addEventListener('mouseenter', function () {
        open(p);
      });
      p.addEventListener('focus', function () {
        open(p);
      });
    });
    row.addEventListener('mouseleave', reset);
  }

  /* -----------------------------------------------------------------------------
     4. Message index (chapter 04): hover/focus a row swaps the active row, the
        crossfade image, the quote, the CTA, and the "Shown" line.
     ----------------------------------------------------------------------------- */
  function wireMessageIndex(container) {
    if (!container) return;
    var rows = container.querySelectorAll('[data-msgrow]');
    var quoteEl = container.querySelector('[data-msgquote]');
    var ctaEl = container.querySelector('[data-msgcta]');
    var shownEl = container.querySelector('[data-msgshown]');
    var imageLayers = container.parentElement
      ? container.parentElement.querySelectorAll('[data-msgbg]')
      : [];
    if (!rows.length) return;

    function activate(row) {
      var id = row.getAttribute('data-msgrow');
      rows.forEach(function (r) {
        r.classList.toggle('is-active', r === row);
      });
      imageLayers.forEach(function (layer) {
        layer.classList.toggle('is-active', layer.getAttribute('data-msgbg') === id);
      });
      if (quoteEl) quoteEl.textContent = row.getAttribute('data-quote') || '';
      if (ctaEl) {
        ctaEl.textContent = 'Shop ' + (row.getAttribute('data-name') || '') + ' →';
        ctaEl.setAttribute('href', row.getAttribute('href'));
      }
      if (shownEl) shownEl.textContent = 'Shown: “' + (row.getAttribute('data-design') || '') + '”';
    }

    rows.forEach(function (row) {
      row.addEventListener('mouseenter', function () {
        activate(row);
      });
      row.addEventListener('focus', function () {
        activate(row);
      });
    });
  }

  /* -----------------------------------------------------------------------------
     5. Founder note: staggered row reveal + signature clip-path wipe. Reduced
        motion is handled by CSS (afroyla-home.css forces the signature to its open
        state); rows still stagger in on scroll either way, matching the sim.
     ----------------------------------------------------------------------------- */
  function wireFounder(key) {
    var rows = Array.prototype.slice.call(document.querySelectorAll('[data-fu="' + key + '"]'));
    if (!rows.length) return;
    var wipes = document.querySelectorAll('[data-sig="' + key + '"] [data-sigwipe]');
    var box = document.querySelector('[data-sig="' + key + '"]');

    once(rows[0].parentElement, function () {
      rise(rows, null, 120);
      setTimeout(
        function () {
          wipes.forEach(function (w) {
            w.classList.add('is-open');
          });
          if (box) {
            setTimeout(function () {
              box.classList.add('is-visible');
            }, 1500);
          }
        },
        rows.length * 120
      );
    });
  }

  /* -----------------------------------------------------------------------------
     6. Mobile shoppable dots: fade + pop in once their card enters view.
     ----------------------------------------------------------------------------- */
  function wireMobileDots(root) {
    (root || document).querySelectorAll('[data-hs-photo]').forEach(function (plate) {
      var dots = plate.querySelectorAll('[data-dot]');
      if (!dots.length) return;
      once(plate, function () {
        dots.forEach(function (a, i) {
          setTimeout(function () {
            a.classList.add('is-in');
            var disc = a.querySelector('[data-disc]');
            if (disc && !REDUCE) disc.classList.add('is-pop');
          }, i * 120);
        });
      });
    });
  }

  /* -----------------------------------------------------------------------------
     7. Swipe rails: native touch scroll; pointer-drag for mouse/pen so the deck is
        swipeable on desktop too (a drag must not also fire the link it lands on).
     ----------------------------------------------------------------------------- */
  function wireSwipe(root) {
    (root || document).querySelectorAll('[data-swipe]').forEach(function (track) {
      var down = false;
      var startX = 0;
      var startLeft = 0;
      var moved = false;

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
        track.style.cursor = 'grabbing';
        if (track.setPointerCapture) track.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        track.scrollLeft = startLeft - dx;
        e.preventDefault();
      });
      function end() {
        down = false;
        track.style.cursor = 'grab';
        // Deferred past this tick so the synchronous click that follows pointerup
        // still sees moved=true (suppressing click-through on a dragged link);
        // clears in time for any later, unrelated click on the track.
        setTimeout(function () {
          moved = false;
        }, 0);
      }
      track.addEventListener('pointerup', end);
      track.addEventListener('pointerleave', end);
      track.addEventListener(
        'click',
        function (e) {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
      track.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
      track.style.cursor = 'grab';
      track.style.userSelect = 'none';
    });
  }

  /* -----------------------------------------------------------------------------
     8. Spine label 05 "gap" placement — the one case that crosses section
        boundaries (S5 caption band -> founder note kicker), so it stays JS-driven.
     ----------------------------------------------------------------------------- */
  function placeGapSpine() {
    var label = document.querySelector('[data-cspine-gap="true"]');
    if (!label || !label.offsetParent) return;
    // Scoped to the story-wall section specifically — an unscoped page-wide
    // `[data-mediacap]` query returns chapter 02's caption band first (it's
    // earlier in the DOM), which put the "05 — Our Story" label's gap math
    // against the wrong element.
    var mediaCap = document.querySelector('.afl-home-story [data-mediacap]');
    var kicker = document.querySelector('[data-fu="f"]');
    if (!mediaCap || !kicker) return;

    var parentRect = label.offsetParent.getBoundingClientRect();
    var gapTop = mediaCap.getBoundingClientRect().bottom;
    var gapBottom = kicker.getBoundingClientRect().top;
    if (gapBottom <= gapTop) return;
    var mid = gapTop + (gapBottom - gapTop) / 2;
    label.style.top = mid - parentRect.top + 'px';
  }

  /* -----------------------------------------------------------------------------
     9. Curtain-deck sticky, :has()-less fallback (see afroyla-home.css, C1 fix).
        Marks each chapter's `.shopify-section` wrapper with the same class + z the
        CSS `:has()` rules already target — redundant (and harmless) wherever
        `:has()` is supported, load-bearing where it isn't.
     ----------------------------------------------------------------------------- */
  var DECK_CHAPTERS = [
    ['.afl-home-hero', 1],
    ['.afl-home-drop', 2],
    ['.afl-home-looks', 3],
    ['.afl-home-msg', 4],
    ['.afl-home-story', 5],
    ['.afl-home-founder', 6],
  ];

  function markDeckSections(root) {
    DECK_CHAPTERS.forEach(function (entry) {
      (root || document).querySelectorAll(entry[0]).forEach(function (section) {
        var wrap = section.parentElement;
        if (!wrap || !wrap.classList.contains('shopify-section')) return;
        wrap.classList.add('afl-home-deck-wrap');
        wrap.style.setProperty('--deck-z', entry[1]);
      });
    });
  }

  /* -----------------------------------------------------------------------------
     Wire (or re-wire) every interactive group scoped to `root` — used for the
     initial boot (root = document) and for a single reloaded section in the theme
     editor (root = that section's own element, see the shopify:section:load
     listener below). Every function here either takes a root/element param or
     already no-ops safely when its target isn't found within `root`, so calling
     this again for an unrelated section is a harmless no-op, not a double-wire.
     ----------------------------------------------------------------------------- */
  function wireAll(root) {
    var scope = root || document;

    fitAll();
    scope.querySelectorAll('[data-hs-photo] img').forEach(function (img) {
      img.addEventListener('load', fitAll);
    });

    scope.querySelectorAll('[data-hs-photo]').forEach(wirePlateHover);
    scope.querySelectorAll('[data-mediacap]').forEach(wireCaptionRise);
    wireLooksRow(scope.querySelector('[data-looksrow]'));
    wireMessageIndex(scope.querySelector('[data-msgindex]'));
    if (scope.querySelector('[data-fu="f"]')) wireFounder('f');
    if (scope.querySelector('[data-fu="fm"]')) wireFounder('fm');
    wireMobileDots(scope);
    wireSwipe(scope);

    markDeckSections(scope);
  }

  /* -----------------------------------------------------------------------------
     Boot
     ----------------------------------------------------------------------------- */
  function init() {
    wireAll(document);
    window.addEventListener('resize', fitAll);

    placeGapSpine();
    window.addEventListener('resize', placeGapSpine);
    setTimeout(placeGapSpine, 900);

    // Theme editor: a merchant editing this chapter's settings replaces its DOM
    // subtree and fires this on `document` with the fresh element as the target.
    // Re-wire just that subtree — every wireX() above is scoped/guarded, so this
    // never re-attaches listeners to sections that didn't change.
    document.addEventListener('shopify:section:load', function (e) {
      if (!e.target || typeof e.target.querySelectorAll !== 'function') return;
      wireAll(e.target);
      placeGapSpine();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
